import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatOptionModule, ThemePalette } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslocoModule } from '@jsverse/transloco';
import { produce } from 'immer';
import { distinctUntilChanged } from 'rxjs';
import { RichTextEditorComponent } from 'shared/feature-rich-text-editor';
import { ColorPickerComponent } from 'shared/ui-common';

import { Attachment, FlowchartTextBlockDto, GuidelineLinkDto, LinkElementType, Reference } from '@treetop/shared/model';
import { GuidelineElementSection, GuidelineElementType } from '@treetop/shared/reference-numbering';

import { FlowchartConfig } from '../../../../../../../shared/feature-flowchart/src/lib/infrastructure/base/flowchart.config';
import { DividerComponent } from '../../../../../../../shared/ui-common/src/lib/components/divider/divider.component';
import { GuidelineSidebarBasics, GuidelineSidebarBasicsComponent } from '../../guideline-sidebar-basics/guideline-sidebar-basics.component';

@Component({
  standalone: true,
  imports: [
    RichTextEditorComponent,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    TranslocoModule,
    MatOptionModule,
    MatSelectModule,
    MatTabsModule,
    ColorPickerComponent,
    GuidelineSidebarBasicsComponent,
    DividerComponent,
  ],
  selector: 'flowchart-sidebar-textblock',
  templateUrl: './flowchart-sidebar-textblock.component.html',
  styleUrls: ['./flowchart-sidebar-textblock.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlowchartSidebarTextBlockComponent implements OnInit {
  protected _sidebarElement = signal<FlowchartTextBlockDto | undefined>(undefined);
  @Input() public set sidebarElement(sidebarElement: FlowchartTextBlockDto) {
    this._sidebarElement.set(sidebarElement);
    this.setInitialStyles();
  }

  @Output() public flowchartElementDataChanged = new EventEmitter<Partial<FlowchartTextBlockDto>>();

  protected placeholder: string;
  protected color: ThemePalette = 'primary';
  protected borderStyle = 'solid';
  protected borderColor = new FormControl(null);
  protected backgroundColor = new FormControl(null);
  protected renderDescription = true;

  public ngOnInit() {
    this.borderColor.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      const value = this.borderColor.value;
      value && this.flowchartElementDataChanged.emit({ customStyling: { borderColor: value } });
    });

    this.backgroundColor.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      const value = this.backgroundColor.value;
      value && this.flowchartElementDataChanged.emit({ customStyling: { backgroundColor: value } });
    });
  }

  private setInitialStyles() {
    const customStyling = this._sidebarElement().customStyling ?? {};

    this.borderColor.setValue(customStyling.borderColor ?? FlowchartConfig.textBlockNode.styles.borderColor);

    this.backgroundColor.setValue(customStyling.backgroundColor ?? FlowchartConfig.textBlockNode.styles.backgroundColor);

    this.borderStyle = customStyling.borderStyle ?? FlowchartConfig.textBlockNode.styles.borderStyle;
  }

  protected onDescriptionChanged(description: string) {
    this.flowchartElementDataChanged.emit({ content: description !== '' ? description : undefined });
  }

  protected onReferencesChanged(references: Reference[]) {
    this.flowchartElementDataChanged.emit({ references });
  }

  protected onAttachmentsChanged(attachments: Attachment[]) {
    this.flowchartElementDataChanged.emit({ attachments });
  }

  protected onLinksChanged(links: GuidelineLinkDto[]) {
    this.flowchartElementDataChanged.emit({
      links: links.map((link) => ({ ...link, fromElementUniqueId: this._sidebarElement().uniqueId })),
    });
  }

  protected onBorderStyleChange() {
    this.flowchartElementDataChanged.emit({ customStyling: { borderStyle: this.borderStyle } });
  }

  protected handleBasicsChanged(basics: GuidelineSidebarBasics) {
    const newComponent = produce({ ...this.sidebarElement }, (draft) => {
      draft.references = basics.references;
      draft.links = basics.links;
      draft.attachments = basics.attachments;
      draft.resourceTags = basics.resourceTags;
    });

    this.flowchartElementDataChanged.emit(newComponent);
  }

  protected readonly GuidelineElementSection = GuidelineElementSection;
  protected readonly GuidelineElementType = GuidelineElementType;
  protected readonly GuidelineLinkTypes = LinkElementType;
  protected readonly FlowchartConfig = FlowchartConfig;
}
